import React, { PureComponent } from 'react';

import PropTypes from 'prop-types';
import {
    CSSTransition,
} from 'react-transition-group';

import { useFeatureFlag } from 'wanderu-analytics';
import { Redirect } from 'wanderu-component-react-router-link';
import appSettings from 'wanderu-config';
import {
    localize,
} from 'wanderu-i18n';
import {
    buildSearchUrlPath,
} from 'wanderu-utils';

import {
    SEARCH_CACHE_DAYS,
    ONE_WAY_SEARCH,
    ROUND_TRIP_SEARCH,
    SEARCH_TYPES,
} from '../../constants';
import WidgetBody from './WidgetBody/WidgetBody';
import Events from './events/SearchWidgetEvents';
import {
    normalizeDates,
    getDefaultDate,
    getPredictionMetadata,
    getLocalStorageSearchHistory,
    setLocalStorageSearchHistory,
} from './utils';

import styles from './SearchWidget.scss';

class SearchWidget extends PureComponent {
    static propTypes = {
        clientSideRouting: PropTypes.bool,
        ctzChecked: PropTypes.bool,
        currency: PropTypes.string,
        defaultSearchType: PropTypes.oneOf(SEARCH_TYPES),
        departDate: PropTypes.string,
        deployClicktripzOffering: PropTypes.func,
        destinationInput: PropTypes.string,
        destinationPlaceId: PropTypes.string,
        destinationPrediction: PropTypes.arrayOf(PropTypes.shape({
            placeId: PropTypes.string,
            text: PropTypes.string,
        })),
        disableButton: PropTypes.bool,
        fetchPlaceDetails: PropTypes.func,
        fetchPrediction: PropTypes.func,
        i18nMoment: PropTypes.func,
        initialDestinationInput: PropTypes.string,
        initialOriginInput: PropTypes.string,
        locale: PropTypes.string,
        localStorageKey: PropTypes.string,
        multiPaxEnabled: PropTypes.bool,
        onDestinationChange: PropTypes.func,
        onDestinationInputChange: PropTypes.func,
        onOriginChange: PropTypes.func,
        onOriginInputChange: PropTypes.func,
        onPassengerChange: PropTypes.func,
        onToggleClicktripz: PropTypes.func,
        onUserInputChange: PropTypes.func,
        onUserSearch: PropTypes.func,
        originInput: PropTypes.string,
        originPlaceId: PropTypes.string,
        originPrediction: PropTypes.arrayOf(PropTypes.shape({
            placeId: PropTypes.string,
            text: PropTypes.string,
        })),
        passengersInput: PropTypes.string,
        queries: PropTypes.object,
        readFromLocalStorage: PropTypes.bool,
        reduxDestinationInput: PropTypes.string,
        reduxDestinationPlaceId: PropTypes.string,
        reduxOriginInput: PropTypes.string,
        reduxOriginPlaceId: PropTypes.string,
        reduxPassengersInput: PropTypes.string,
        returnDate: PropTypes.string,
        searchPage: PropTypes.bool,
        setDestinationInput: PropTypes.func,
        setDestinationPlaceId: PropTypes.func,
        setOriginInput: PropTypes.func,
        setOriginPlaceId: PropTypes.func,
        setPassengersInput: PropTypes.func,
        sticky: PropTypes.bool,
        stickyClassName: PropTypes.string,
        tabBlack: PropTypes.bool,
        tabBlackWhite: PropTypes.bool,
        title: PropTypes.string,
    };

    static defaultProps = {
        clientSideRouting: true,
        defaultSearchType: ONE_WAY_SEARCH,
        deployClicktripzOffering: () => undefined,
        fetchPrediction: () => undefined,
        multiPaxEnabled: false,
        onDestinationChange: () => undefined,
        onDestinationInputChange: () => undefined,
        onOriginChange: () => undefined,
        onOriginInputChange: () => undefined,
        onPassengerChange: () => undefined,
        onUserInputChange: () => undefined,
        onUserSearch: () => undefined,
        queries: {},
        readFromLocalStorage: true,
        sticky: false,
        tabBlack: false,
        tabBlackWhite: false,
        title: '',
    };

    constructor(props) {
        super(props);

        const {
            departDate,
            destinationPrediction,
            originInput,
            originPrediction,
            returnDate,
        } = this.props;

        const defaultDepartDate = getDefaultDate();
        const {
            departDate: normalizedDepartDate,
            returnDate: normalizedReturnDate,
        } = normalizeDates(departDate, returnDate);

        const searchType = this.getSearchType({
            hasDepartCity: !!originInput,
            hasReturnDate: !!normalizedReturnDate,
        });

        this.state = {
            departDate: normalizedDepartDate || defaultDepartDate,
            destinationPrediction: destinationPrediction,
            emptyFields: {
                missingDepartDate: false,
                missingDestination: false,
                missingOrigin: false,
                missingReturnDate: false,
            },
            equalInputs: false,
            internalRedirect: false,
            minimized: true,
            originPrediction: originPrediction,
            returnDate: normalizedReturnDate,
            searchType,
        };
    }

    async componentDidMount() {
        const searchHistory = this.getLocalStorageSearchHistory() || {};
        const {
            departDate,
            destinationInput: localDestinationInput,
            destinationPlaceId: localDestinationPlaceId,
            originInput: localOriginInput,
            originPlaceId: localOriginPlaceId,
            passengersInput: localPassengersInput,
            returnDate,
        } = searchHistory;

        const {
            destinationInput,
            destinationPlaceId,
            localStorageKey,
            multiPaxEnabled,
            originInput,
            originPlaceId,
            passengersInput,
            readFromLocalStorage,
            setDestinationInput,
            setDestinationPlaceId,
            setOriginInput,
            setOriginPlaceId,
            setPassengersInput,
        } = this.props;

        if (readFromLocalStorage && localStorageKey) {
            if (!destinationInput && !destinationPlaceId) {
                setDestinationInput(localDestinationInput);
                setDestinationPlaceId(localDestinationPlaceId);
            }
            if (!originInput && !originPlaceId) {
                setOriginInput(localOriginInput);
                setOriginPlaceId(localOriginPlaceId);
            }

            // Gate the rehydration behind the same flag the dropdown uses so that, if
            // search-multi-pax is killed, returning users who previously picked N>1 don't
            // get ?p=N silently injected into searches with no UI affordance to change it.
            if (multiPaxEnabled && !passengersInput) {
                setPassengersInput(localPassengersInput);
            }

            if ((!!originInput || !!localOriginInput) && !returnDate) {
                this.setState(() => ({
                    searchType: ONE_WAY_SEARCH,
                }));
            }
        }

        this.setState((oldState) => {
            const isDefaultDepartDate = !this.props.departDate;
            let departDateToSet = departDate;
            if (!departDate || !isDefaultDepartDate) {
                departDateToSet = oldState.departDate;
            }

            const searchType = this.getSearchType({
                hasDepartCity: !!originInput,
                hasReturnDate: !!(oldState.returnDate || returnDate),
            });

            return {
                departDate: departDateToSet,
                returnDate: oldState.returnDate || returnDate,
                searchType,
            };
        }, () => {
            this.initializeOriginInput();
            this.initializeDestinationInput();
            this.initializeEvents();
        });
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            departDate,
            destinationInput,
            destinationPlaceId,
            initialDestinationInput,
            initialOriginInput,
            originInput,
            originPlaceId,
            passengersInput,
            reduxDestinationInput,
            reduxDestinationPlaceId,
            reduxOriginInput,
            reduxOriginPlaceId,
            reduxPassengersInput,
            returnDate,
            setDestinationInput,
            setDestinationPlaceId,
            setOriginInput,
            setOriginPlaceId,
            setPassengersInput,
        } = this.props;
        const {
            returnDate: stateReturnDate,
            searchType,
        } = this.state;
        const {
            departDate: prevDepartDate,
            destinationInput: prevDestinationInput,
            initialDestinationInput: prevInitialDestinationInput,
            initialOriginInput: prevInitialOriginInput,
            originInput: prevOriginInput,
            returnDate: prevReturnDate,
        } = prevProps;

        if (prevDepartDate !== departDate) {
            this.setState(() => ({ departDate }));
        }

        if (prevReturnDate !== returnDate) {
            this.setState(() => ({ returnDate }));
        }

        if (!prevInitialOriginInput
            && !originInput
            && !reduxOriginInput
            && initialOriginInput) {
            setOriginInput(initialOriginInput);
        }

        if (!prevInitialDestinationInput
            && !destinationInput
            && !reduxDestinationInput
            && initialDestinationInput) {
            setDestinationInput(initialDestinationInput);
        }

        if (originInput
            && reduxOriginInput
            && originInput !== reduxOriginInput) {
            setOriginInput(originInput);
        }

        if (passengersInput
            && reduxPassengersInput
            && passengersInput !== reduxPassengersInput) {
            setPassengersInput(passengersInput);
        }

        /*
         * set search type to one-way if loading originInput from local storage
         * and there's no return date in local storage or state
         */
        if (!prevOriginInput
            && originInput
            && !prevDestinationInput
            && destinationInput
            && !returnDate
            && !stateReturnDate) {
            this.setState(() => ({ searchType: ONE_WAY_SEARCH }));
        }

        if (destinationInput
            && reduxDestinationInput
            && destinationInput !== reduxDestinationInput) {
            setDestinationInput(destinationInput);
        }

        if (originPlaceId
            && reduxOriginPlaceId
            && originPlaceId !== reduxOriginPlaceId) {
            setOriginPlaceId(originPlaceId);
        }

        if (destinationPlaceId
            && reduxDestinationPlaceId
            && destinationPlaceId !== reduxDestinationPlaceId) {
            setDestinationPlaceId(destinationPlaceId);
        }

        if (prevState.searchType === ROUND_TRIP_SEARCH && searchType === ONE_WAY_SEARCH) {
            this.setState(() => ({ returnDate: undefined }));
        }

        const { sticky } = this.props;
        const { sticky: prevSticky } = prevProps;

        if (prevSticky && !sticky) {
            this.setState(() => ({ minimized: true }));
        }
    }

    static getDerivedStateFromProps(props, state) {
        return {
            destinationPrediction: props.destinationPrediction
                || (props.destinationInput && state.destinationPrediction),
            originPrediction: props.originPrediction
                || (props.originInput && state.originPrediction),
        };
    }

    getSearchType({ hasDepartCity, hasReturnDate }) {
        const { defaultSearchType } = this.props;
        const noReturn = hasDepartCity
            ? ONE_WAY_SEARCH
            : defaultSearchType;

        /*
            Passing state into props is generally considered an anti-pattern because changes to the
            prop are not observable. In this case, however, this is actually what we want: pass a default search
            type prop into state on mount, and then allow state to manage it from there.
        */
        return hasReturnDate
            ? ROUND_TRIP_SEARCH
            : noReturn;
    }

    initializeEvents() {
        const {
            destinationInput,
            originInput,
        } = this.props;

        const {
            departDate,
            returnDate,
            searchType,
        } = this.state;

        Events.loaded({
            destination: destinationInput,
            is_one_way: searchType === ONE_WAY_SEARCH,
            origin: originInput,
            outbound_date: departDate,
            return_date: returnDate,
        });
    }

    initializeOriginInput() {
        const {
            originInput,
            originPlaceId,
        } = this.props;

        return this.initializeInput(originInput, originPlaceId);
    }

    initializeDestinationInput() {
        const {
            destinationInput,
            destinationPlaceId,
        } = this.props;

        return this.initializeInput(destinationInput, destinationPlaceId);
    }

    initializeInput(input, placeId) {
        if (!input && !placeId) {
            return;
        }

        const {
            fetchPlaceDetails,
            fetchPrediction,
            locale,
        } = this.props;

        if (input) {
            fetchPrediction({
                input,
                locale,
                meta: getPredictionMetadata(),
            });
        } else {
            fetchPlaceDetails(placeId);
        }
    }

    onErrorMessageClear = () => {
        this.setState(() => ({
            emptyFields: {
                missingDepartDate: false,
                missingDestination: false,
                missingOrigin: false,
                missingReturnDate: false,
            },
            equalInputs: false,
        }));
    };

    onDepartDateChange = (departDate) => {
        this.setState(() => ({ departDate }));

        this.props.onUserInputChange();
    };

    onReturnDateChange = (returnDate) => {
        this.setState(() => ({ returnDate }));

        this.props.onUserInputChange();
    };

    onTabChange = (searchType) => {
        this.setState(() => ({ searchType }));

        this.props.onUserInputChange();
    };

    getLocalStorageSearchHistory() {
        const {
            localStorageKey: key,
            readFromLocalStorage,
        } = this.props;

        if (!key || !readFromLocalStorage) {
            return undefined;
        }

        return getLocalStorageSearchHistory(key);
    }

    setLocalStorageSearchHistory() {
        const {
            destinationInput,
            destinationPlaceId,
            i18nMoment,
            localStorageKey: key,
            originInput,
            originPlaceId,
            passengersInput,
        } = this.props;

        const {
            departDate,
            returnDate,
        } = this.state;

        const searchHistory = {
            createdAt: i18nMoment().format(),
            departDate: departDate,
            destinationInput,
            destinationPlaceId,
            expiresAt: i18nMoment().add(SEARCH_CACHE_DAYS, 'days').format(),
            originInput,
            originPlaceId,
            passengersInput,
            returnDate: returnDate,
        };

        setLocalStorageSearchHistory(key, searchHistory);
    }

    buildSearchUrl(isNewWindow = false) {
        const {
            clientSideRouting,
            currency,
            destinationInput,
            destinationPlaceId,
            locale,
            multiPaxEnabled,
            originInput,
            originPlaceId,
            passengersInput,
            queries,
        } = this.props;

        const {
            departDate,
            returnDate,
        } = this.state;

        const searchUrlRequest = {
            arrivePlace: destinationInput,
            arrivePlaceId: destinationPlaceId,
            departDate: departDate,
            departPlace: originInput,
            departPlaceId: originPlaceId,
            passengers: multiPaxEnabled
                ? passengersInput
                : undefined,
            returnDate: returnDate,
        };

        if (!clientSideRouting || isNewWindow) {
            let baseUrl = appSettings.env.wanderuEnv === 'prod'
                ? 'www.wanderu.com'
                : `${appSettings.env.wanderuEnv}.wanderu.com`;

            return (
                `//${baseUrl}/${locale}`
                + buildSearchUrlPath(searchUrlRequest, {
                    cur: currency,
                    ...queries,
                })
            );
        }

        return buildSearchUrlPath(searchUrlRequest, queries);
    }

    onUserSearch = async (isValid, errors) => {
        const {
            clientSideRouting,
            deployClicktripzOffering,
            onUserSearch,
            searchPage,
            ctzChecked,
        } = this.props;

        const {
            departDate,
            returnDate,
            searchType,
        } = this.state;

        if (!isValid) {
            return this.setState(() => ({ ...errors }));
        }

        const deployCtzLeavebehind = ctzChecked && !searchPage;

        this.setLocalStorageSearchHistory();
        const url = this.buildSearchUrl(deployCtzLeavebehind);

        if (deployCtzLeavebehind) {
            window.open(url);

            const dates = {
                departDate,
            };

            if (searchType === ROUND_TRIP_SEARCH) {
                dates.returnDate = returnDate;
            }

            return deployClicktripzOffering(dates, ctzChecked);
        }

        if (clientSideRouting) {
            onUserSearch();
            return this.setState(
                () => ({ internalRedirect: true }),
                () => this.setState(() => ({ internalRedirect: false })),
            );
        }

        window.location.assign(url);
        onUserSearch();

        return undefined;
    };

    onToggleMinimized = () => {
        this.setState((currentState) => ({ minimized: !currentState.minimized }));
    };

    toggleClicktrips = () => {
        const {
            ctzChecked,
            onToggleClicktripz,
        } = this.props;

        onToggleClicktripz(!ctzChecked);
        Events.clickTripzPreference(!ctzChecked);
    };

    render() {
        const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            clientSideRouting: _clientSideRouting,
            sticky,
            stickyClassName,
            ...otherProps
        } = this.props;

        const {
            internalRedirect,
            minimized,
            ...otherState
        } = this.state;

        return (
            <>
                {sticky && !minimized && (
                    <div className={styles.deemphasize} />
                )}
                {internalRedirect
                    && <Redirect
                        href={this.buildSearchUrl()}
                        queries={this.props.queries}
                        push
                    />}
                <WidgetBody
                    {...otherProps}
                    {...otherState}
                    minimized={minimized}
                    onDepartDateChange={this.onDepartDateChange}
                    onErrorMessageClear={this.onErrorMessageClear}
                    onReturnDateChange={this.onReturnDateChange}
                    onTabChange={this.onTabChange}
                    onToggle={this.onToggleMinimized}
                    onUserSearch={this.onUserSearch}
                    toggleClicktrips={this.toggleClicktrips}
                />
                <CSSTransition
                    classNames={{
                        enter: styles.enter,
                        enterActive: styles.enterActive,
                        enterDone: styles.enterDone,
                    }}
                    in={sticky}
                    timeout={{ enter: 300, exit: 0 }}
                    mountOnEnter
                    unmountOnExit
                >
                    <div className={stickyClassName}>
                        <WidgetBody
                            {...otherProps}
                            {...otherState}
                            minimized={minimized}
                            onDepartDateChange={this.onDepartDateChange}
                            onErrorMessageClear={this.onErrorMessageClear}
                            onReturnDateChange={this.onReturnDateChange}
                            onTabChange={this.onTabChange}
                            onToggle={this.onToggleMinimized}
                            onUserSearch={this.onUserSearch}
                            toggleClicktrips={this.toggleClicktrips}
                            sticky
                        />
                    </div>
                </CSSTransition>
            </>
        );
    }
}

function SearchWidgetWithFlag(props) {
    const { isEnabled } = useFeatureFlag('search-multi-pax', false, 'multi-pax');
    return <SearchWidget {...props} multiPaxEnabled={isEnabled} />;
}

export { SearchWidget };
export default localize(SearchWidgetWithFlag, ['i18nMoney', 'i18nMoment', 't'], 'widget');
